import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { getAuthMe, authLogout, authLoginUrl, type AuthUser } from '@/lib/api';

const AUTH_STORAGE_KEY = 'homeland_auth_user';

function getStoredUser(): AuthUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw) as unknown;
    if (data && typeof data === 'object' && 'id' in data && 'email' in data && 'role' in data) {
      const d = data as { id: string; email: string; role: string; name?: string; picture_url?: string };
      if (['admin', 'tutor', 'student'].includes(d.role))
        return { id: d.id, email: d.email, role: d.role as AuthUser['role'], name: d.name, picture_url: d.picture_url };
    }
  } catch {
    /* ignore */
  }
  return null;
}

function setStoredUser(user: AuthUser | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (user) localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    else localStorage.removeItem(AUTH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  /** Reload user from API (e.g. after profile update) */
  refreshUser: () => Promise<void>;
  /** Full URL to redirect for Google sign-in */
  loginUrl: string;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const u = await getAuthMe();
      setUser(u);
      setStoredUser(u);
    } catch {
      setUser(null);
      setStoredUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(() => {
    if (typeof window === 'undefined') return;
    const currentPath = window.location.pathname + window.location.search;
    const url = new URL(authLoginUrl, window.location.origin);
    url.searchParams.set('returnTo', currentPath);
    window.location.href = url.toString();
  }, []);

  const logout = useCallback(async () => {
    await authLogout();
    setUser(null);
    setStoredUser(null);
    window.location.href = '/';
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const u = await getAuthMe();
      setUser(u);
      setStoredUser(u);
    } catch {
      setUser(null);
      setStoredUser(null);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    loginUrl: authLoginUrl,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
